import { useId, type ComponentPropsWithRef, type MouseEvent, type ReactNode } from 'react'
import { Link, type LinkProps } from 'react-router'
import { cx } from '../lib/cx'
import { Icon, type IconName } from './Icon'
import './Button.css'

export type ButtonVariant = 'primary' | 'secondary' | 'text' | 'destructive'

interface ButtonLookProps {
  /** primary (default), secondary, text or destructive. */
  variant?: ButtonVariant
  /** Leading icon, decorative. */
  icon?: IconName
  /** Trailing icon, decorative. */
  iconEnd?: IconName
  /** Stretch to the width of its container (pinned primary actions). */
  fullWidth?: boolean
  children: ReactNode
}

export interface ButtonProps extends Omit<ComponentPropsWithRef<'button'>, 'disabled' | 'children'>, ButtonLookProps {
  /** A spinner replaces the label; the width stays fixed and the button cannot be pressed. */
  loading?: boolean
  /** Shown at 38% opacity and cannot be pressed. It stays focusable so the reason can be read. */
  disabled?: boolean
  /** Why the button is disabled, shown beneath it (PRD section 17: never a silent dead button). */
  disabledReason?: string
}

function tick() {
  try {
    navigator.vibrate?.(8)
  } catch {
    // Haptics are a nicety; some browsers refuse them.
  }
}

/**
 * The PRD button: 48 px tall; pressed adds an 8% darker overlay (and a light haptic
 * tick for primary); loading swaps the label for a spinner at a fixed width;
 * disabled is 38% opacity with the reason shown beneath.
 */
export function Button({
  variant = 'primary',
  icon,
  iconEnd,
  fullWidth = false,
  loading = false,
  disabled = false,
  disabledReason,
  className,
  children,
  onClick,
  type = 'button',
  ...rest
}: ButtonProps) {
  const reasonId = useId()
  const blocked = disabled || loading
  const showReason = disabled && Boolean(disabledReason)
  const describedBy = [rest['aria-describedby'], showReason ? reasonId : undefined].filter(Boolean).join(' ') || undefined

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    if (blocked) {
      event.preventDefault()
      return
    }
    if (variant === 'primary') tick()
    onClick?.(event)
  }

  const button = (
    <button
      {...rest}
      type={type}
      className={cx(
        'btn',
        `btn--${variant}`,
        fullWidth && 'btn--full',
        loading && 'btn--loading',
        disabled && 'btn--disabled',
        className,
      )}
      aria-disabled={blocked || undefined}
      aria-busy={loading || undefined}
      aria-describedby={describedBy}
      onClick={handleClick}
    >
      <span className="btn__content">
        {icon && <Icon name={icon} />}
        <span className="btn__label">{children}</span>
        {iconEnd && <Icon name={iconEnd} />}
      </span>
      {loading && <span className="btn__spinner spinner" aria-hidden="true" />}
    </button>
  )

  if (disabledReason === undefined) return button
  return (
    <div className={cx('btn-group', fullWidth && 'btn-group--full')}>
      {button}
      {showReason && (
        <p id={reasonId} className="btn__reason">
          {disabledReason}
        </p>
      )}
    </div>
  )
}

export interface ButtonLinkProps extends Omit<LinkProps, 'children' | 'className'>, ButtonLookProps {
  className?: string
}

/** A link that looks like a button, for navigation ("Open the calculator"). */
export function ButtonLink({ variant = 'primary', icon, iconEnd, fullWidth = false, className, children, ...rest }: ButtonLinkProps) {
  return (
    <Link {...rest} className={cx('btn', `btn--${variant}`, fullWidth && 'btn--full', className)}>
      <span className="btn__content">
        {icon && <Icon name={icon} />}
        <span className="btn__label">{children}</span>
        {iconEnd && <Icon name={iconEnd} />}
      </span>
    </Link>
  )
}
